// @tg-seq: 10989
// @architect-approved: HARDCODED_COLORS - Auto-whitelisted for white-label theme system (2026-06-16)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Button,
    IconButton,
    Typography,
    Paper,
    Grid,
    Chip,
    Divider,
    TextField,
    Menu,
    MenuItem,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Fade,
    Badge,
} from '@mui/material';
import {
    Close as CloseIcon,
    Phone as PhoneIcon,
    PhoneInTalk as PhoneInTalkIcon,
    Search as SearchIcon,
    Note as NoteIcon,
    Speed as SpeedIcon,
    Business as BusinessIcon,
    Settings as SettingsIcon,
    Accessibility as AccessibilityIcon,
    Dashboard as DashboardIcon,
    Save as SaveIcon,
    Clear as ClearIcon,
    MoreVert as MoreVertIcon,
    Voicemail as VoicemailIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    OpenInFull as OpenInFullIcon,
    CloseFullscreen as CloseFullscreenIcon,
} from '@mui/icons-material';
import { getTsxFileName } from '../../utils/componentIdentity';
import { navigateToComponent } from '../../utils/directoryNavigation';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/useAuth';
import { apiRequestBySeq } from '../../utils/apiService';
import UniversalContactSearch from './scr_tgc_11201_universal_contact_search';
import SpeedDialer from './scr_tgc_10874_speed_dialer';
import VoiceRecordingOverlay from './scr_tgc_11200_voice_recording_overlay';
import TimeStudyDashboard from './scr_tgc_10979_timestudy_dashboard_overlay';
import EventSubmissionForm from './scr_tgc_10673_event_submission_questions';
import EventSubmissionMasterRecord from './scr_tgc_10265_event_submission_master_record';

interface PhoneToolProps {
    // Controlled mode (e.g. mounted once by PlatformHeader): parent owns open state.
    // Omit both props to use the component's own trigger button (uncontrolled/tab usage).
    open?: boolean;
    onClose?: () => void;
    _task?: any;
    _agenticContext?: any;
}

const Self = ({
    open: controlledOpen,
    onClose: controlledOnClose,
    _task,
    _agenticContext,
}: PhoneToolProps) => {
    const isControlled = controlledOpen !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const mainOpen = isControlled ? !!controlledOpen : internalOpen;
    const closeMain = () => {
        if (isControlled) {
            controlledOnClose?.();
        } else {
            setInternalOpen(false);
        }
    };

    // Floating/movable + collapse-expand: the main console used to be a modal
    // MUI <Dialog> (backdrop-blocking). It's now a non-modal, draggable Paper
    // panel so the user can keep it open while interacting with the page
    // behind it, reposition it anywhere, and collapse it to a small title bar
    // when it's in the way (without fully closing/losing its state).
    const EXPANDED_WIDTH = 1100;
    const MINIMIZED_WIDTH = 300;
    const MINIMIZED_HEIGHT = 48;
    const [phoneMinimized, setPhoneMinimized] = useState(false);
    const [panelPos, setPanelPos] = useState(() => ({
        x: Math.max(20, (window.innerWidth - EXPANDED_WIDTH) / 2),
        y: 60,
    }));
    const dragStateRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });
    const [isDragging, setIsDragging] = useState(false);

    const handleDragStart = (e: React.MouseEvent) => {
        dragStateRef.current = {
            dragging: true,
            offsetX: e.clientX - panelPos.x,
            offsetY: e.clientY - panelPos.y,
        };
        setIsDragging(true);
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!dragStateRef.current.dragging) return;
            setPanelPos({
                x: e.clientX - dragStateRef.current.offsetX,
                y: e.clientY - dragStateRef.current.offsetY,
            });
        };
        const handleMouseUp = () => {
            if (dragStateRef.current.dragging) {
                dragStateRef.current.dragging = false;
                setIsDragging(false);
            }
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    const [speedDialOpen, setSpeedDialOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [notesOpen, setNotesOpen] = useState(false);
    const [dashboardOpen, setDashboardOpen] = useState(false);
    const [meetingOpen, setMeetingOpen] = useState(false);
    const [selectedMeeting, setSelectedMeeting] = useState<any>(null);
    const [contextMenu, setContextMenu] = useState<null | HTMLElement>(null);
    const [callNotes, setCallNotes] = useState('');

    // Speed Dial Voicemail: Voice Recording Overlay (scr_tgc_11200) is opened
    // alongside Call Entry Notes (mirrors handleSpeedDialCall's simultaneous
    // tel:+notes launch below), with its target contact tracked here so the
    // resulting transcription can be saved as a TEL.Voice_Message addressed to
    // the right pfuser once recording completes.
    const { pfUser, activeOrganizationUUID } = useAuth();

    // Signed-in user's Personal Callsign (ADM.pfuser via sp_PFUser_Security_Controller),
    // shown in the sidebar in place of a hardcoded extension number.
    const [personalCallsign, setPersonalCallsign] = useState<string | null>(null);
    useEffect(() => {
        if (!pfUser?.user_uuid) return;
        (async () => {
            try {
                const response: any = await apiRequestBySeq(11077, 1, {
                    manage: 5,
                    pfuser_uuid: pfUser.user_uuid,
                });
                const row = response?.data?.[0];
                setPersonalCallsign(row?.personal_callsign || null);
            } catch (err) {
                console.error('[Phone Tool] Failed to load personal callsign:', err);
            }
        })();
    }, [pfUser?.user_uuid]);

    const [voicemailOpen, setVoicemailOpen] = useState(false);
    const [voicemailTarget, setVoicemailTarget] = useState<{
        name: string;
        pfuserUuid?: string;
    } | null>(null);

    // Voicemail Inbox (playback/transcript view) - lists TEL.Voice_Message rows
    // addressed to the signed-in pfuser, enriched server-side (2026-10-06 SP
    // ALTER) with the caller's display name and the linked transcription's
    // audio URL + transcript text so no second round-trip is needed per row.
    const [voicemailInboxOpen, setVoicemailInboxOpen] = useState(false);
    const [voicemailMessages, setVoicemailMessages] = useState<any[]>([]);
    const [voicemailInboxLoading, setVoicemailInboxLoading] = useState(false);
    const [voicemailUnreadCount, setVoicemailUnreadCount] = useState(0);

    // Speed Dial Timeclock: opens the same TimeStudy dashboard overlay
    // (scr_tgc_10979) the app header's "My Tasks / Timeclock" icon opens,
    // triggered from a contact's Timeclock icon in Speed Dial. Target is
    // tracked for a future per-contact filter — today it shows the
    // signed-in user's own time data, same as the header's instance.
    const [timeclockOpen, setTimeclockOpen] = useState(false);
    const [timeclockTarget, setTimeclockTarget] = useState<{
        name: string;
        pfuserUuid?: string;
    } | null>(null);

    // Timeclock overlay: floating/draggable/collapsible, same pattern as the
    // main console panel above (separate state so both can be open/positioned
    // independently).
    const TIMECLOCK_WIDTH = 1000;
    const TIMECLOCK_MINIMIZED_WIDTH = 300;
    const TIMECLOCK_MINIMIZED_HEIGHT = 48;
    const [timeclockMinimized, setTimeclockMinimized] = useState(false);
    const [timeclockPanelPos, setTimeclockPanelPos] = useState(() => ({
        x: Math.max(20, (window.innerWidth - TIMECLOCK_WIDTH) / 2),
        y: 90,
    }));
    const timeclockDragStateRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });
    const [isTimeclockDragging, setIsTimeclockDragging] = useState(false);

    const handleTimeclockDragStart = (e: React.MouseEvent) => {
        timeclockDragStateRef.current = {
            dragging: true,
            offsetX: e.clientX - timeclockPanelPos.x,
            offsetY: e.clientY - timeclockPanelPos.y,
        };
        setIsTimeclockDragging(true);
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!timeclockDragStateRef.current.dragging) return;
            setTimeclockPanelPos({
                x: e.clientX - timeclockDragStateRef.current.offsetX,
                y: e.clientY - timeclockDragStateRef.current.offsetY,
            });
        };
        const handleMouseUp = () => {
            if (timeclockDragStateRef.current.dragging) {
                timeclockDragStateRef.current.dragging = false;
                setIsTimeclockDragging(false);
            }
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    // ERS Request overlay (scr_tgc_10673) — opened in-place (NOT a route
    // navigation) so the Phone Tool stays mounted underneath, mirroring the
    // Timeclock overlay pattern below.
    const [ersRequestOpen, setErsRequestOpen] = useState(false);

    // Open Events overlay (scr_tgc_10265, Event Submission Master Record) —
    // same in-place overlay pattern as ERS Request. Lets ops view/open
    // submitted ERS requests (and other Universal Event submissions) without
    // leaving the Phone Tool. Filtering down to just "open" events is a
    // follow-up task, not yet implemented.
    const [openEventsOpen, setOpenEventsOpen] = useState(false);

    const navigate = useNavigate();

    // Mock navigation by sequence (will be replaced with actual sequence-based routing)
    const navigateBySeq = (seq: number) => {
        console.log(`[MOCK] Would navigate to sequence: ${seq}`);
        // TODO: Implement actual sequence-based navigation with manifest lookup
        // For now, using example paths for demonstration
        const mockRoutes: { [key: number]: string } = {
            10814: '/organization-registry',
            10420: '/component-registry',
            10032: '/context-action-system',
            10500: '/organization-manager',
            10010: '/pnl-levers-library',
            10994: '/meeting-master', // Meeting Master
        };
        const route = mockRoutes[seq];
        if (route) {
            navigate(route);
        } else {
            console.warn(`No route found for sequence ${seq}`);
        }
    };

    // Open the Windows-registered tel: handler (Microsoft Phone Link when set as
    // the system default) - same protocol pattern used by Meeting Master's dial-in button.
    // phoneNumber is optional - omitted when just opening the app (dashboard button),
    // populated when dialing a specific contact from Speed Dial.
    const handleOpenPhoneService = (phoneNumber?: string) => {
        window.location.href = phoneNumber ? `tel:${phoneNumber}` : 'tel:';
    };

    // Quick Actions gallery (Phone Dashboard) routes into the Activity Browser
    // (scr_tgc_10016, the Task Composer we're iteratively designing/enhancing)
    // via sequence resolution, rather than just logging the click.
    const navigateToActivityBrowser = () => {
        setDashboardOpen(false);
        navigateToComponent(10016, navigate);
    };

    // ERS Request Quick Action opens the standalone dynamic Event
    // Submission form (scr_tgc_10673) as an in-place overlay (not a route
    // navigation) so operations can submit a request (e.g. equipment repair)
    // via the question set configured for that Event Type in the Lifecycle
    // Event Type Library admin tab (scr_tgc_10672), without losing the Phone
    // Tool underneath.
    const openErsRequest = () => {
        setDashboardOpen(false);
        setErsRequestOpen(true);
    };

    // Open Events Quick Action opens the Event Submission Master Record
    // (scr_tgc_10265) as an in-place overlay so ops can review/open submitted
    // ERS requests (and other Universal Event submissions) without leaving
    // the Phone Tool. Which events count as "open" will be filtered down in
    // a follow-up task -- this currently shows the full submitted list.
    const openOpenEvents = () => {
        setDashboardOpen(false);
        setOpenEventsOpen(true);
    };

    // Called when a contact's phone icon is clicked from within the Speed Dial
    // overlay - launches the tel: handler (with the real number when Speed Dial
    // has one, e.g. an External contact's external_phone), closes Speed Dial,
    // and opens the Call Entry Notes overlay so the user can jot notes during
    // the live call.
    const handleSpeedDialCall = (_contactName: string, phoneNumber?: string) => {
        handleOpenPhoneService(phoneNumber);
        setSpeedDialOpen(false);
        setNotesOpen(true);
    };

    // Called when a contact's voicemail icon is clicked from Speed Dial — opens
    // the Voice Recording Overlay (11200, real MediaRecorder + Azure Speech
    // transcription) AND Call Entry Notes together, same simultaneous-launch
    // pattern as handleSpeedDialCall. The overlay is tagged with the
    // 'Speed Dial Voicemail' source channel so it hides its ideation-only
    // fields (Business Subject/Data Use/Agentic Function) and so the resulting
    // LLM.Voice_Transcription_Queue row is distinguishable from dictation use.
    const handleSpeedDialVoicemail = (contactName: string, pfuserUuid?: string) => {
        setVoicemailTarget({ name: contactName, pfuserUuid });
        setSpeedDialOpen(false);
        setNotesOpen(true);
        setVoicemailOpen(true);
    };

    // Called when a contact's Timeclock icon is clicked from Speed Dial —
    // closes Speed Dial and opens the shared TimeStudy dashboard overlay
    // (same component/behavior as the app header's "My Tasks / Timeclock"
    // icon, see PlatformHeader.tsx's handleTimeclockClick).
    const handleSpeedDialTimeclock = (contactName: string, pfuserUuid?: string) => {
        setTimeclockTarget({ name: contactName, pfuserUuid });
        setSpeedDialOpen(false);
        setTimeclockOpen(true);
    };

    // Fires once the overlay finishes recording + transcribing. Creates the
    // TEL.Voice_Message business record (who it's to/from, soft-deletable,
    // listened/unlistened) referencing the transcription row for its audio URL
    // + transcript text — the transcription queue itself is left untouched.
    const handleVoicemailTranscriptionComplete = async (
        _transcription: string,
        _confidence: number,
        transcriptionUuid: string,
        blobUuid?: string,
    ) => {
        if (!voicemailTarget?.pfuserUuid || !pfUser?.user_uuid) {
            console.warn(
                '[Phone Tool] Voicemail transcription completed but no target pfuser was set - skipping TEL.Voice_Message creation.',
            );
            return;
        }
        try {
            await apiRequestBySeq(10874, 1, {
                manage: 1,
                organization_uuid: activeOrganizationUUID === 'ALL' ? null : activeOrganizationUUID,
                from_pfuser_uuid: pfUser.user_uuid,
                to_pfuser_uuid: voicemailTarget.pfuserUuid,
                transcription_uuid: transcriptionUuid,
                blob_uuid: blobUuid,
                ttp_id: 0,
            });
        } catch (err) {
            console.error('[Phone Tool] Failed to save voice message:', err);
        }
        // Just left a voicemail-for-someone-else; doesn't change MY unread count,
        // so no refresh needed here.
    };

    // Voicemail Inbox: unread count badge (fetched on mount + whenever the
    // inbox dialog closes, so the header-style badge convention stays fresh
    // without a manual refresh button).
    const fetchVoicemailUnreadCount = useCallback(async () => {
        if (!pfUser?.user_uuid) return;
        try {
            const res: any = await apiRequestBySeq(10874, 1, {
                manage: 7,
                to_pfuser_uuid: pfUser.user_uuid,
            });
            setVoicemailUnreadCount(res?.stats?.unread_count ?? res?.stats?.Unread_Count ?? 0);
        } catch (err) {
            console.error('[Phone Tool] Failed to fetch voicemail unread count:', err);
        }
    }, [pfUser?.user_uuid]);

    useEffect(() => {
        fetchVoicemailUnreadCount();
    }, [fetchVoicemailUnreadCount]);

    const fetchVoicemailMessages = useCallback(async () => {
        if (!pfUser?.user_uuid) return;
        setVoicemailInboxLoading(true);
        try {
            const res: any = await apiRequestBySeq(10874, 1, {
                manage: 5,
                to_pfuser_uuid: pfUser.user_uuid,
            });
            setVoicemailMessages(res?.data ?? []);
        } catch (err) {
            console.error('[Phone Tool] Failed to fetch voicemail inbox:', err);
        } finally {
            setVoicemailInboxLoading(false);
        }
    }, [pfUser?.user_uuid]);

    const handleOpenVoicemailInbox = () => {
        setVoicemailInboxOpen(true);
        fetchVoicemailMessages();
    };

    const handleCloseVoicemailInbox = () => {
        setVoicemailInboxOpen(false);
        fetchVoicemailUnreadCount();
    };

    const handleMarkVoicemailListened = async (voiceMessageUuid: string) => {
        setVoicemailMessages((prev) =>
            prev.map((m) =>
                m.voice_message_uuid === voiceMessageUuid ? { ...m, is_listened: true } : m,
            ),
        );
        try {
            await apiRequestBySeq(10874, 1, {
                manage: 2,
                voice_message_uuid: voiceMessageUuid,
                is_listened: true,
            });
        } catch (err) {
            console.error('[Phone Tool] Failed to mark voicemail as listened:', err);
        }
        fetchVoicemailUnreadCount();
    };

    const handleDeleteVoicemail = async (voiceMessageUuid: string) => {
        setVoicemailMessages((prev) =>
            prev.filter((m) => m.voice_message_uuid !== voiceMessageUuid),
        );
        try {
            await apiRequestBySeq(10874, 1, {
                manage: 3,
                voice_message_uuid: voiceMessageUuid,
            });
        } catch (err) {
            console.error('[Phone Tool] Failed to delete voicemail:', err);
        }
        fetchVoicemailUnreadCount();
    };

    // Mock Meeting/Task Data (will be replaced with DB queries)
    const upcomingMeetings = [
        {
            id: 1,
            wo: '623165',
            asset: '123456',
            maintenancePlan: 'Drop & Pick',
            appointmentETA: '2018-08-01 08:00',
            requestedCompletion: '2018-08-01 10:00',
            currentETC: '2018-08-01 11:00',
            location: 'Apple Ford Shakopee',
            status: 'Scheduled',
        },
        {
            id: 2,
            wo: '270046',
            asset: '223122',
            maintenancePlan: 'PM Components',
            appointmentETA: '2018-08-01 09:00',
            requestedCompletion: '2018-08-01 11:00',
            currentETC: '2018-08-01 12:00',
            location: 'Customer Site A',
            status: 'Approved',
        },
        {
            id: 3,
            wo: '268845',
            asset: '223322',
            maintenancePlan: 'Brakes',
            appointmentETA: '2018-08-01 13:00',
            requestedCompletion: '2018-08-01 15:00',
            currentETC: '2018-08-01 16:00',
            location: 'Customer Site B',
            status: 'Shop Repair',
        },
    ];

    const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        setContextMenu(event.currentTarget);
    };

    const handleContextClose = () => {
        setContextMenu(null);
    };

    const handleWriteToCRM = () => {
        console.log('Writing to CRM:', callNotes);
        // TODO: POST /api/crm-notes { manage: 1, notes: callNotes }
        handleContextClose();
    };

    const handleUpdateTask = () => {
        console.log('Updating task notes:', callNotes);
        // TODO: POST /api/task-notes { manage: 2, notes: callNotes }
        handleContextClose();
    };

    const handleClearCallStatus = () => {
        console.log('Clearing call status');
        setCallNotes('');
        // TODO: POST /api/call-status { manage: 3, status: 'cleared' }
        handleContextClose();
    };

    const handleMeetingClick = (meeting: any) => {
        // Option 1: Open Teams-style detail view
        setSelectedMeeting(meeting);
        setMeetingOpen(true);
    };

    const handleJoinMeeting = (meeting: any) => {
        // Option 2: Navigate to Meeting Master for full experience
        console.log('[Phone Tool] Navigating to Meeting Master for:', meeting);
        // TODO: Pass meeting context via route state
        navigateBySeq(10994); // Meeting Master
        setDashboardOpen(false);
    };

    return (
        <>
            {/* Trigger Button - only rendered when uncontrolled (e.g. registered as a tab) */}
            {!isControlled && (
                <IconButton
                    onClick={() => setInternalOpen(true)}
                    sx={{
                        bgcolor: 'success.main',
                        color: 'success.contrastText',
                        '&:hover': { bgcolor: 'success.dark' },
                        boxShadow: 2,
                    }}
                    title="Open Virtual Office Phone"
                >
                    <PhoneIcon />
                </IconButton>
            )}

            {/* Main Phone Console — floating, draggable, collapsible panel
                (NOT a modal Dialog) so it can stay open alongside normal page
                interaction, be repositioned anywhere via its title bar, and
                collapse to a small bar instead of fully closing. */}
            {mainOpen && (
                <Paper
                    elevation={12}
                    sx={{
                        position: 'fixed',
                        left: panelPos.x,
                        top: panelPos.y,
                        width: phoneMinimized
                            ? MINIMIZED_WIDTH
                            : Math.min(EXPANDED_WIDTH, window.innerWidth - 40),
                        height: phoneMinimized ? MINIMIZED_HEIGHT : '85vh',
                        maxHeight: '85vh',
                        zIndex: 1250,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        bgcolor: 'background.default',
                        transition: isDragging ? 'none' : 'width 0.15s, height 0.15s',
                    }}
                >
                    {/* Title bar — drag handle + collapse/expand + close */}
                    <Box
                        onMouseDown={handleDragStart}
                        sx={{
                            bgcolor: 'grey.900',
                            color: 'grey.100',
                            px: 2,
                            py: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: isDragging ? 'grabbing' : 'grab',
                            userSelect: 'none',
                            flexShrink: 0,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <PhoneInTalkIcon sx={{ color: 'success.light', fontSize: 20 }} />
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                            >
                                Virtual Office Phone
                                {phoneMinimized && personalCallsign ? ` — ${personalCallsign}` : ''}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPhoneMinimized((m) => !m);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                title={phoneMinimized ? 'Expand' : 'Collapse'}
                                sx={{ color: 'grey.300' }}
                            >
                                {phoneMinimized ? (
                                    <OpenInFullIcon fontSize="small" />
                                ) : (
                                    <CloseFullscreenIcon fontSize="small" />
                                )}
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    closeMain();
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                title="Close"
                                sx={{
                                    color: 'error.contrastText',
                                    bgcolor: 'error.main',
                                    '&:hover': { bgcolor: 'error.dark' },
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>

                    {!phoneMinimized && (
                        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                            <DialogContent sx={{ p: 0, height: '100%' }}>
                                <Grid container sx={{ height: '100%' }}>
                                    {/* Left Sidebar - User Status */}
                                    <Grid
                                        sx={{ bgcolor: 'grey.800', color: 'grey.100', p: 2 }}
                                        size={2.5}
                                    >
                                        <Box
                                            sx={{
                                                mb: 3,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src="/branding/techgacity-mark.svg"
                                                alt="TechGacity mark"
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography variant="h6" sx={{ color: 'grey.100' }}>
                                                Techgacity
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'grey.400' }}
                                            >
                                                Callsign:
                                            </Typography>
                                            <Typography
                                                variant="h5"
                                                sx={{ color: 'primary.main', fontWeight: 700 }}
                                            >
                                                {personalCallsign || '—'}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* Main Content Area - "Phone Display Screen" */}
                                    <Grid
                                        sx={{ bgcolor: 'grey.100', p: 3, overflowY: 'auto' }}
                                        size={7}
                                    >
                                        {/* Phone Display Panel */}
                                        <Paper
                                            elevation={8}
                                            sx={{
                                                bgcolor: 'grey.900',
                                                p: 3,
                                                borderRadius: 2,
                                                minHeight: '500px',
                                                backgroundImage:
                                                    'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(30,30,30,0.95) 100%)',
                                            }}
                                        >
                                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                                <Typography
                                                    variant="h5"
                                                    sx={{ color: 'primary.light', mb: 1 }}
                                                >
                                                    Virtual Office Phone
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: 'grey.400' }}
                                                >
                                                    McDonald's Menu Navigation System
                                                </Typography>
                                            </Box>

                                            <Divider sx={{ bgcolor: 'grey.700', mb: 3 }} />

                                            {/* Main Speed Dial Buttons */}
                                            <Grid container spacing={2}>
                                                <Grid size={6}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        size="large"
                                                        startIcon={<SpeedIcon />}
                                                        onClick={() => setSpeedDialOpen(true)}
                                                        sx={{
                                                            py: 3,
                                                            bgcolor: 'primary.main',
                                                            '&:hover': { bgcolor: 'primary.dark' },
                                                        }}
                                                    >
                                                        Speed Dial
                                                    </Button>
                                                </Grid>

                                                <Grid size={6}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        size="large"
                                                        startIcon={<SearchIcon />}
                                                        onClick={() => setSearchOpen(true)}
                                                        sx={{
                                                            py: 3,
                                                            bgcolor: 'info.main',
                                                            '&:hover': { bgcolor: 'info.dark' },
                                                        }}
                                                    >
                                                        CollabCore Search
                                                    </Button>
                                                </Grid>

                                                <Grid size={6}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        size="large"
                                                        startIcon={<NoteIcon />}
                                                        onClick={() => setNotesOpen(true)}
                                                        sx={{
                                                            py: 3,
                                                            bgcolor: 'warning.main',
                                                            '&:hover': { bgcolor: 'warning.dark' },
                                                        }}
                                                    >
                                                        Call Entry Notes
                                                    </Button>
                                                </Grid>

                                                <Grid size={6}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        size="large"
                                                        startIcon={<DashboardIcon />}
                                                        onClick={() => {
                                                            setDashboardOpen(true);
                                                            closeMain();
                                                        }}
                                                        sx={{
                                                            py: 3,
                                                            bgcolor: 'success.main',
                                                            '&:hover': { bgcolor: 'success.dark' },
                                                        }}
                                                    >
                                                        My Dashboard
                                                    </Button>
                                                </Grid>
                                            </Grid>

                                            <Divider sx={{ bgcolor: 'grey.700', my: 3 }} />

                                            {/* Quick Actions Panel - moved inline from the old My Dashboard
                                    dialog gallery so it's always visible in the phone body
                                    instead of requiring a separate dialog. Grey header bar
                                    doubles as a small icon-only gallery/index; cards below
                                    scroll horizontally so the panel keeps a fixed height. */}
                                            <Box sx={{ borderRadius: 1, overflow: 'hidden' }}>
                                                <Box
                                                    sx={{
                                                        bgcolor: 'grey.300',
                                                        px: 2,
                                                        py: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ color: 'grey.900', fontWeight: 600 }}
                                                    >
                                                        Quick Actions
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <PhoneIcon
                                                            sx={{
                                                                color: 'success.main',
                                                                fontSize: 20,
                                                            }}
                                                        />
                                                        <SettingsIcon
                                                            sx={{
                                                                color: 'success.dark',
                                                                fontSize: 20,
                                                            }}
                                                        />
                                                        <AccessibilityIcon
                                                            sx={{
                                                                color: 'primary.main',
                                                                fontSize: 20,
                                                            }}
                                                        />
                                                        <ClearIcon
                                                            sx={{
                                                                color: 'error.main',
                                                                fontSize: 20,
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        bgcolor: 'grey.900',
                                                        p: 2,
                                                        display: 'flex',
                                                        gap: 2,
                                                        overflowX: 'auto',
                                                    }}
                                                >
                                                    <Paper
                                                        elevation={3}
                                                        sx={{
                                                            minWidth: 160,
                                                            p: 2,
                                                            textAlign: 'center',
                                                            bgcolor: 'success.main',
                                                            color: 'success.contrastText',
                                                            cursor: 'pointer',
                                                            flexShrink: 0,
                                                            '&:hover': {
                                                                bgcolor: 'success.dark',
                                                                transform: 'scale(1.05)',
                                                            },
                                                            transition: 'all 0.2s',
                                                        }}
                                                        onClick={openOpenEvents}
                                                    >
                                                        <PhoneIcon sx={{ fontSize: 40, mb: 1 }} />
                                                        <Typography variant="subtitle1">
                                                            Open Events
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            View/Open Submissions
                                                        </Typography>
                                                    </Paper>

                                                    <Paper
                                                        elevation={3}
                                                        sx={{
                                                            minWidth: 160,
                                                            p: 2,
                                                            textAlign: 'center',
                                                            bgcolor: 'success.light',
                                                            color: 'success.contrastText',
                                                            cursor: 'pointer',
                                                            flexShrink: 0,
                                                            '&:hover': {
                                                                bgcolor: 'success.main',
                                                                transform: 'scale(1.05)',
                                                            },
                                                            transition: 'all 0.2s',
                                                        }}
                                                        onClick={openErsRequest}
                                                    >
                                                        <SettingsIcon
                                                            sx={{ fontSize: 40, mb: 1 }}
                                                        />
                                                        <Typography variant="subtitle1">
                                                            ERS REQUEST
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            Request/Complaint
                                                        </Typography>
                                                    </Paper>

                                                    <Paper
                                                        elevation={3}
                                                        sx={{
                                                            minWidth: 160,
                                                            p: 2,
                                                            textAlign: 'center',
                                                            bgcolor: 'primary.main',
                                                            color: 'primary.contrastText',
                                                            cursor: 'pointer',
                                                            flexShrink: 0,
                                                            '&:hover': {
                                                                bgcolor: 'primary.dark',
                                                                transform: 'scale(1.05)',
                                                            },
                                                            transition: 'all 0.2s',
                                                        }}
                                                        onClick={navigateToActivityBrowser}
                                                    >
                                                        <AccessibilityIcon
                                                            sx={{ fontSize: 40, mb: 1 }}
                                                        />
                                                        <Typography variant="subtitle1">
                                                            Request
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            Appointment Change
                                                        </Typography>
                                                    </Paper>

                                                    <Paper
                                                        elevation={3}
                                                        sx={{
                                                            minWidth: 160,
                                                            p: 2,
                                                            textAlign: 'center',
                                                            bgcolor: 'error.main',
                                                            color: 'error.contrastText',
                                                            cursor: 'pointer',
                                                            flexShrink: 0,
                                                            '&:hover': {
                                                                bgcolor: 'error.dark',
                                                                transform: 'scale(1.05)',
                                                            },
                                                            transition: 'all 0.2s',
                                                        }}
                                                        onClick={navigateToActivityBrowser}
                                                    >
                                                        <ClearIcon sx={{ fontSize: 40, mb: 1 }} />
                                                        <Typography variant="subtitle1">
                                                            Request Cancel
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            Appointment
                                                        </Typography>
                                                    </Paper>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>

                                    {/* Right Sidebar - Actions */}
                                    <Grid
                                        sx={{
                                            bgcolor: 'background.paper',
                                            p: 2,
                                            borderLeft: 1,
                                            borderColor: 'divider',
                                        }}
                                        size={2.5}
                                    >
                                        <Paper
                                            elevation={2}
                                            sx={{ p: 2, mb: 2, bgcolor: 'warning.light' }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'warning.contrastText' }}
                                            >
                                                Status Box Area
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'warning.contrastText',
                                                    display: 'block',
                                                    mt: 1,
                                                }}
                                            >
                                                (Greg to redesign - should be as tall as status box)
                                            </Typography>
                                        </Paper>

                                        <Button
                                            fullWidth
                                            variant="contained"
                                            startIcon={<SearchIcon />}
                                            sx={{ mb: 1, bgcolor: 'grey.500' }}
                                        >
                                            Search
                                        </Button>

                                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                            <IconButton
                                                onClick={() => handleOpenPhoneService()}
                                                title="Open Microsoft Phone Link"
                                                sx={{
                                                    bgcolor: 'success.main',
                                                    color: 'success.contrastText',
                                                    flex: 1,
                                                }}
                                            >
                                                <PhoneIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={handleOpenVoicemailInbox}
                                                title="Voicemail Inbox"
                                                sx={{
                                                    bgcolor: 'secondary.main',
                                                    color: 'secondary.contrastText',
                                                    flex: 1,
                                                }}
                                            >
                                                <Badge
                                                    color="error"
                                                    badgeContent={voicemailUnreadCount}
                                                    max={99}
                                                >
                                                    <VoicemailIcon />
                                                </Badge>
                                            </IconButton>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                        </Box>
                    )}
                </Paper>
            )}

            {/* Speed Dial Overlay - Speed Dialer mockup (scr_tgc_10874) */}
            <Dialog
                open={speedDialOpen}
                onClose={() => setSpeedDialOpen(false)}
                maxWidth="xl"
                fullWidth
                slots={{
                    transition: Fade,
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        bgcolor: 'primary.dark',
                        color: 'primary.contrastText',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h6">Speed Dial</Typography>
                    <IconButton
                        onClick={() => setSpeedDialOpen(false)}
                        sx={{ color: 'primary.contrastText' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent>
                    <SpeedDialer
                        onCall={handleSpeedDialCall}
                        onVoicemail={handleSpeedDialVoicemail}
                        onTimeclock={handleSpeedDialTimeclock}
                    />
                </DialogContent>
            </Dialog>

            {/* Voice Recording Overlay (scr_tgc_11200) — controlled-open, triggered
                by Speed Dial's voicemail icon via handleSpeedDialVoicemail. Hidden
                FAB trigger since Phone Tool owns the open/close lifecycle here. */}
            <VoiceRecordingOverlay
                context={{
                    sourceChannel: 'Speed Dial Voicemail',
                    sourceReference: voicemailTarget?.pfuserUuid,
                }}
                controlledOpen={voicemailOpen}
                onOpenChange={setVoicemailOpen}
                hideTrigger
                maxDurationSeconds={180}
                onTranscriptionComplete={handleVoicemailTranscriptionComplete}
            />

            {/* Timeclock Overlay — opened from Speed Dial's Timeclock icon via
                handleSpeedDialTimeclock. Reuses the same TimeStudy dashboard
                overlay the app header's "My Tasks / Timeclock" icon opens
                (scr_tgc_10979). Floating/draggable/collapsible, same
                non-modal <Paper> + title-bar pattern as the main console
                panel above (previously a hand-rolled fixed-position Box with
                a hardcoded zIndex that didn't participate in MUI's modal
                stacking order). */}
            {timeclockOpen && (
                <Paper
                    elevation={12}
                    sx={{
                        position: 'fixed',
                        left: timeclockPanelPos.x,
                        top: timeclockPanelPos.y,
                        width: timeclockMinimized
                            ? TIMECLOCK_MINIMIZED_WIDTH
                            : Math.min(TIMECLOCK_WIDTH, window.innerWidth - 40),
                        height: timeclockMinimized ? TIMECLOCK_MINIMIZED_HEIGHT : '85vh',
                        maxHeight: '85vh',
                        zIndex: 1250,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        bgcolor: 'background.default',
                        transition: isTimeclockDragging ? 'none' : 'width 0.15s, height 0.15s',
                    }}
                >
                    {/* Title bar — drag handle + collapse/expand + close */}
                    <Box
                        onMouseDown={handleTimeclockDragStart}
                        sx={{
                            bgcolor: 'grey.900',
                            color: 'grey.100',
                            px: 2,
                            py: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: isTimeclockDragging ? 'grabbing' : 'grab',
                            userSelect: 'none',
                            flexShrink: 0,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                            >
                                Timeclock
                                {timeclockTarget?.name ? ` — ${timeclockTarget.name}` : ''}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setTimeclockMinimized((m) => !m);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                title={timeclockMinimized ? 'Expand' : 'Collapse'}
                                sx={{ color: 'grey.300' }}
                            >
                                {timeclockMinimized ? (
                                    <OpenInFullIcon fontSize="small" />
                                ) : (
                                    <CloseFullscreenIcon fontSize="small" />
                                )}
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setTimeclockOpen(false);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                title="Close"
                                sx={{
                                    color: 'error.contrastText',
                                    bgcolor: 'error.main',
                                    '&:hover': { bgcolor: 'error.dark' },
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>

                    {!timeclockMinimized && (
                        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                            <TimeStudyDashboard />
                        </Box>
                    )}
                </Paper>
            )}

            {/* ERS Request Overlay — Event Submission form (scr_tgc_10673),
                opened via the Quick Actions "ERS REQUEST" button above.
                Uses a real MUI <Dialog> (Portal + modal stacking manager),
                NOT a hand-rolled fixed-position Box with a hardcoded zIndex —
                the Box approach doesn't participate in MUI's modal stacking
                order, so it rendered UNDERNEATH the main Phone Tool Dialog
                it's nested inside of. A real Dialog stacks correctly on top,
                same as the Speed Dial / CollabCore Search overlays below.
                initialLifecycleTypeId=15 presets/locks the form to
                ERS_Roadside ("Emergency Repair Serviced: Roadside") so this
                quick action always lands directly on that Event Type list. */}
            <Dialog
                open={ersRequestOpen}
                onClose={() => setErsRequestOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <IconButton
                    onClick={() => setErsRequestOpen(false)}
                    sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent>
                    <EventSubmissionForm initialLifecycleTypeId={15} />
                </DialogContent>
            </Dialog>

            {/* Open Events Overlay — Event Submission Master Record (scr_tgc_10265),
                opened via the Quick Actions "Open Events" button above. Reuses the
                same real MUI <Dialog> stacking pattern as ERS Request above (so it
                renders correctly on top of the Phone Tool it's nested inside of). */}
            <Dialog
                open={openEventsOpen}
                onClose={() => setOpenEventsOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <IconButton
                    onClick={() => setOpenEventsOpen(false)}
                    sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent sx={{ height: '80vh' }}>
                    <EventSubmissionMasterRecord />
                </DialogContent>
            </Dialog>

            {/* CollabCore Search Overlay - Universal Contact Search (scr_tgc_11201) */}
            <Dialog
                open={searchOpen}
                onClose={() => setSearchOpen(false)}
                maxWidth="lg"
                fullWidth
                slots={{
                    transition: Fade,
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        bgcolor: 'info.dark',
                        color: 'info.contrastText',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h6">CollabCore Search</Typography>
                    <IconButton
                        onClick={() => setSearchOpen(false)}
                        sx={{ color: 'info.contrastText' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent>
                    <UniversalContactSearch />
                </DialogContent>
            </Dialog>

            {/* Call Entry Notes Overlay — when launched alongside the Voice
                Recording Overlay (Speed Dial Voicemail flow, voicemailOpen
                true), this becomes a non-modal side panel anchored to the
                right instead of a centered blocking modal, so the user can
                operate Speak-to-AI/Start Recording without closing Notes
                first. Standalone "live call" notes (handleSpeedDialCall,
                voicemailOpen false) keep the original centered modal. */}
            <Dialog
                open={notesOpen}
                onClose={() => setNotesOpen(false)}
                maxWidth="sm"
                fullWidth={!voicemailOpen}
                hideBackdrop={voicemailOpen}
                disableEnforceFocus={voicemailOpen}
                disableScrollLock={voicemailOpen}
                sx={
                    voicemailOpen
                        ? {
                              pointerEvents: 'none',
                              '& .MuiDialog-container': {
                                  alignItems: 'flex-start',
                                  justifyContent: 'flex-end',
                              },
                          }
                        : undefined
                }
                slotProps={
                    voicemailOpen
                        ? {
                              paper: {
                                  sx: {
                                      pointerEvents: 'auto',
                                      m: 2,
                                      width: 400,
                                      maxHeight: 'calc(100% - 32px)',
                                  },
                              },
                          }
                        : undefined
                }
                slots={{
                    transition: Fade,
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        bgcolor: 'warning.dark',
                        color: 'warning.contrastText',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h6">Call Entry Notes</Typography>
                    <IconButton
                        onClick={() => setNotesOpen(false)}
                        sx={{ color: 'warning.contrastText' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={8}
                        label="Call Notes"
                        variant="outlined"
                        value={callNotes}
                        onChange={(e) => setCallNotes(e.target.value)}
                        placeholder="Enter call details, customer feedback, or action items..."
                        sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleWriteToCRM}
                            sx={{ flex: 1, bgcolor: 'success.main' }}
                        >
                            Save to CRM
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<ClearIcon />}
                            onClick={() => setCallNotes('')}
                            sx={{ flex: 1 }}
                        >
                            Clear
                        </Button>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            onClick={handleUpdateTask}
                            sx={{ color: 'info.main', borderColor: 'info.main' }}
                        >
                            Update Task Notes
                        </Button>
                        <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            onClick={handleClearCallStatus}
                            sx={{ color: 'error.main', borderColor: 'error.main' }}
                        >
                            Clear Call Status
                        </Button>
                        <IconButton onClick={handleContextMenu} size="small">
                            <MoreVertIcon />
                        </IconButton>
                    </Box>

                    {/* Context Menu */}
                    <Menu
                        anchorEl={contextMenu}
                        open={Boolean(contextMenu)}
                        onClose={handleContextClose}
                    >
                        <MenuItem onClick={handleWriteToCRM}>
                            <ListItemIcon>
                                <SaveIcon fontSize="small" />
                            </ListItemIcon>
                            Write to CRM
                        </MenuItem>
                        <MenuItem onClick={handleUpdateTask}>
                            <ListItemIcon>
                                <NoteIcon fontSize="small" />
                            </ListItemIcon>
                            Update Task Notes
                        </MenuItem>
                        <MenuItem onClick={handleClearCallStatus}>
                            <ListItemIcon>
                                <ClearIcon fontSize="small" />
                            </ListItemIcon>
                            Clear Call Task Status
                        </MenuItem>
                    </Menu>
                </DialogContent>
            </Dialog>

            {/* Voicemail Inbox - playback + transcript view for TEL.Voice_Message
                rows addressed to the signed-in pfuser (scr_tgc_10989 itself owns
                this view rather than a separate registered component, reusing
                the tsx_10874_1 router's existing @Manage 2/3/5/7 operations). */}
            <Dialog
                open={voicemailInboxOpen}
                onClose={handleCloseVoicemailInbox}
                maxWidth="md"
                fullWidth
                slots={{
                    transition: Fade,
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        bgcolor: 'secondary.dark',
                        color: 'secondary.contrastText',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h6">Voicemail Inbox</Typography>
                    <IconButton
                        onClick={handleCloseVoicemailInbox}
                        sx={{ color: 'secondary.contrastText' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent>
                    {voicemailInboxLoading && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                            Loading voicemails...
                        </Typography>
                    )}
                    {!voicemailInboxLoading && voicemailMessages.length === 0 && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                            No voicemails yet.
                        </Typography>
                    )}
                    <List>
                        {voicemailMessages.map((msg) => {
                            const callerName =
                                msg.from_display_name || msg.from_full_name || 'Unknown Caller';
                            const createdDate = msg.created_utc
                                ? new Date(msg.created_utc).toLocaleString()
                                : '';
                            const durationLabel = msg.duration_seconds
                                ? `${Math.round(msg.duration_seconds)}s`
                                : null;
                            return (
                                <Paper
                                    key={msg.voice_message_uuid}
                                    elevation={1}
                                    sx={{
                                        p: 2,
                                        mb: 1.5,
                                        borderLeft: 4,
                                        borderColor: msg.is_listened
                                            ? 'grey.300'
                                            : 'secondary.main',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            mb: 1,
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                {callerName}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                {createdDate}
                                                {durationLabel ? ` · ${durationLabel}` : ''}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                        >
                                            {!msg.is_listened && (
                                                <Chip
                                                    label="Unread"
                                                    size="small"
                                                    color="secondary"
                                                />
                                            )}
                                            {!msg.is_listened && (
                                                <IconButton
                                                    size="small"
                                                    title="Mark as listened"
                                                    onClick={() =>
                                                        handleMarkVoicemailListened(
                                                            msg.voice_message_uuid,
                                                        )
                                                    }
                                                >
                                                    <CheckCircleIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            <IconButton
                                                size="small"
                                                title="Delete"
                                                onClick={() =>
                                                    handleDeleteVoicemail(msg.voice_message_uuid)
                                                }
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    {msg.audio_blob_url && (
                                        <Box
                                            component="audio"
                                            controls
                                            src={msg.audio_blob_url}
                                            onPlay={() => {
                                                if (!msg.is_listened) {
                                                    handleMarkVoicemailListened(
                                                        msg.voice_message_uuid,
                                                    );
                                                }
                                            }}
                                            sx={{
                                                width: '100%',
                                                mb: msg.raw_transcription ? 1 : 0,
                                            }}
                                        />
                                    )}

                                    {msg.raw_transcription && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                                fontStyle: 'italic',
                                                bgcolor: 'grey.50',
                                                p: 1,
                                                borderRadius: 1,
                                            }}
                                        >
                                            &ldquo;{msg.raw_transcription}&rdquo;
                                        </Typography>
                                    )}
                                </Paper>
                            );
                        })}
                    </List>
                </DialogContent>
            </Dialog>

            {/* Phone Dashboard - Virtual Desk Homepage */}
            <Dialog
                open={dashboardOpen}
                onClose={() => setDashboardOpen(false)}
                maxWidth="lg"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            height: '90vh',
                            maxHeight: '90vh',
                            bgcolor: 'background.default',
                        },
                    },
                }}
            >
                <IconButton
                    onClick={() => setDashboardOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        zIndex: 1,
                        bgcolor: 'error.main',
                        color: 'error.contrastText',
                        '&:hover': { bgcolor: 'error.dark' },
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <DialogContent sx={{ p: 4, bgcolor: 'grey.50' }}>
                    {/* Upcoming Meetings/Tasks Grid */}
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}
                        >
                            Upcoming Appointments
                        </Typography>

                        <Paper elevation={2} sx={{ p: 2, bgcolor: 'background.paper' }}>
                            <Box sx={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#1976d2', color: 'white' }}>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>
                                                WO
                                            </th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>
                                                Asset
                                            </th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>
                                                Maintenance Plan
                                            </th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>
                                                Appointment ETA
                                            </th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>
                                                Requested Completion
                                            </th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>
                                                Current ETC
                                            </th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>
                                                Location
                                            </th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>
                                                Status
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {upcomingMeetings.map((meeting) => (
                                            <tr
                                                key={meeting.id}
                                                style={{
                                                    cursor: 'pointer',
                                                    backgroundColor:
                                                        meeting.id % 2 === 0 ? '#f5f5f5' : 'white',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor =
                                                        '#e3f2fd';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor =
                                                        meeting.id % 2 === 0 ? '#f5f5f5' : 'white';
                                                }}
                                            >
                                                <td
                                                    style={{ padding: '12px' }}
                                                    onClick={() => handleJoinMeeting(meeting)}
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                textDecoration: 'underline',
                                                            },
                                                        }}
                                                    >
                                                        {meeting.wo}
                                                    </Box>
                                                </td>
                                                <td
                                                    style={{ padding: '12px' }}
                                                    onClick={() => handleMeetingClick(meeting)}
                                                >
                                                    {meeting.asset}
                                                </td>
                                                <td
                                                    style={{ padding: '12px' }}
                                                    onClick={() => handleMeetingClick(meeting)}
                                                >
                                                    {meeting.maintenancePlan}
                                                </td>
                                                <td
                                                    style={{ padding: '12px' }}
                                                    onClick={() => handleMeetingClick(meeting)}
                                                >
                                                    {meeting.appointmentETA}
                                                </td>
                                                <td
                                                    style={{ padding: '12px' }}
                                                    onClick={() => handleMeetingClick(meeting)}
                                                >
                                                    {meeting.requestedCompletion}
                                                </td>
                                                <td
                                                    style={{ padding: '12px' }}
                                                    onClick={() => handleMeetingClick(meeting)}
                                                >
                                                    {meeting.currentETC}
                                                </td>
                                                <td
                                                    style={{ padding: '12px' }}
                                                    onClick={() => handleMeetingClick(meeting)}
                                                >
                                                    {meeting.location}
                                                </td>
                                                <td
                                                    style={{ padding: '12px' }}
                                                    onClick={() => handleMeetingClick(meeting)}
                                                >
                                                    <Chip
                                                        label={meeting.status}
                                                        size="small"
                                                        sx={{
                                                            bgcolor:
                                                                meeting.status === 'Approved'
                                                                    ? 'success.main'
                                                                    : meeting.status === 'Scheduled'
                                                                      ? 'info.main'
                                                                      : 'warning.main',
                                                            color: 'white',
                                                        }}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Box>
                        </Paper>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Meeting Detail View - Teams-like UI */}
            <Dialog
                open={meetingOpen}
                onClose={() => setMeetingOpen(false)}
                maxWidth="xl"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            height: '95vh',
                            maxHeight: '95vh',
                            bgcolor: 'grey.900',
                        },
                    },
                }}
            >
                <IconButton
                    onClick={() => setMeetingOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        zIndex: 1,
                        bgcolor: 'error.main',
                        color: 'error.contrastText',
                        '&:hover': { bgcolor: 'error.dark' },
                    }}
                >
                    <CloseIcon />
                </IconButton>

                {selectedMeeting && (
                    <DialogContent sx={{ p: 0, height: '100%', bgcolor: 'grey.900' }}>
                        <Grid container sx={{ height: '100%' }}>
                            {/* Main Video/Content Area */}
                            <Grid sx={{ bgcolor: 'grey.800', p: 3, position: 'relative' }} size={9}>
                                {/* Meeting Header */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h4" sx={{ color: 'grey.100', mb: 1 }}>
                                        Work Order: {selectedMeeting.wo}
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ color: 'grey.400' }}>
                                        {selectedMeeting.maintenancePlan} -{' '}
                                        {selectedMeeting.location}
                                    </Typography>
                                </Box>

                                {/* "Video" Area - Virtual Desk View */}
                                <Paper
                                    elevation={8}
                                    sx={{
                                        height: 'calc(100% - 200px)',
                                        bgcolor: 'grey.700',
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundImage:
                                            'linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(30,30,30,0.9) 100%)',
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box sx={{ textAlign: 'center', zIndex: 1 }}>
                                        <BusinessIcon
                                            sx={{ fontSize: 120, color: 'primary.main', mb: 2 }}
                                        />
                                        <Typography variant="h5" sx={{ color: 'grey.100', mb: 1 }}>
                                            {selectedMeeting.location}
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'grey.400' }}>
                                            Asset: {selectedMeeting.asset}
                                        </Typography>
                                    </Box>

                                    {/* Beach Background Overlay Effect */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            height: '30%',
                                            background:
                                                'linear-gradient(to top, rgba(25,118,210,0.3) 0%, transparent 100%)',
                                        }}
                                    />
                                </Paper>

                                {/* Meeting Controls - Teams Style */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 20,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        display: 'flex',
                                        gap: 2,
                                        bgcolor: 'rgba(0,0,0,0.7)',
                                        p: 2,
                                        borderRadius: 3,
                                    }}
                                >
                                    <IconButton sx={{ bgcolor: 'grey.700', color: 'grey.100' }}>
                                        <PhoneIcon />
                                    </IconButton>
                                    <IconButton sx={{ bgcolor: 'grey.700', color: 'grey.100' }}>
                                        <NoteIcon />
                                    </IconButton>
                                    <IconButton sx={{ bgcolor: 'grey.700', color: 'grey.100' }}>
                                        <SettingsIcon />
                                    </IconButton>
                                    <IconButton
                                        sx={{ bgcolor: 'error.main', color: 'error.contrastText' }}
                                    >
                                        <PhoneInTalkIcon />
                                    </IconButton>
                                </Box>
                            </Grid>

                            {/* Right Sidebar - Meeting Details */}
                            <Grid
                                sx={{ bgcolor: 'background.paper', p: 2, overflowY: 'auto' }}
                                size={3}
                            >
                                <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                                    Appointment Details
                                </Typography>

                                <List>
                                    <ListItem>
                                        <ListItemText
                                            primary="Work Order"
                                            secondary={selectedMeeting.wo}
                                            slotProps={{
                                                primary: {
                                                    variant: 'caption',
                                                    color: 'text.secondary',
                                                },

                                                secondary: {
                                                    variant: 'body1',
                                                    color: 'text.primary',
                                                },
                                            }}
                                        />
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <ListItemText
                                            primary="Asset"
                                            secondary={selectedMeeting.asset}
                                            slotProps={{
                                                primary: {
                                                    variant: 'caption',
                                                    color: 'text.secondary',
                                                },

                                                secondary: {
                                                    variant: 'body1',
                                                    color: 'text.primary',
                                                },
                                            }}
                                        />
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <ListItemText
                                            primary="Maintenance Plan"
                                            secondary={selectedMeeting.maintenancePlan}
                                            slotProps={{
                                                primary: {
                                                    variant: 'caption',
                                                    color: 'text.secondary',
                                                },

                                                secondary: {
                                                    variant: 'body1',
                                                    color: 'text.primary',
                                                },
                                            }}
                                        />
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <ListItemText
                                            primary="Appointment ETA"
                                            secondary={selectedMeeting.appointmentETA}
                                            slotProps={{
                                                primary: {
                                                    variant: 'caption',
                                                    color: 'text.secondary',
                                                },

                                                secondary: {
                                                    variant: 'body1',
                                                    color: 'text.primary',
                                                },
                                            }}
                                        />
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <ListItemText
                                            primary="Requested Completion"
                                            secondary={selectedMeeting.requestedCompletion}
                                            slotProps={{
                                                primary: {
                                                    variant: 'caption',
                                                    color: 'text.secondary',
                                                },

                                                secondary: {
                                                    variant: 'body1',
                                                    color: 'text.primary',
                                                },
                                            }}
                                        />
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <ListItemText
                                            primary="Current ETC"
                                            secondary={selectedMeeting.currentETC}
                                            slotProps={{
                                                primary: {
                                                    variant: 'caption',
                                                    color: 'text.secondary',
                                                },

                                                secondary: {
                                                    variant: 'body1',
                                                    color: 'text.primary',
                                                },
                                            }}
                                        />
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <ListItemText
                                            primary="Location"
                                            secondary={selectedMeeting.location}
                                            slotProps={{
                                                primary: {
                                                    variant: 'caption',
                                                    color: 'text.secondary',
                                                },

                                                secondary: {
                                                    variant: 'body1',
                                                    color: 'text.primary',
                                                },
                                            }}
                                        />
                                    </ListItem>
                                </List>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                                    Quick Actions
                                </Typography>

                                <Button
                                    fullWidth
                                    variant="contained"
                                    sx={{ mb: 1, bgcolor: 'success.main' }}
                                >
                                    Confirm ETA
                                </Button>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    sx={{ mb: 1, bgcolor: 'info.main' }}
                                >
                                    Add Notes
                                </Button>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    sx={{ mb: 1, bgcolor: 'warning.main' }}
                                >
                                    Request Change
                                </Button>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    sx={{ mb: 1, color: 'error.main', borderColor: 'error.main' }}
                                >
                                    Cancel Appointment
                                </Button>
                            </Grid>
                        </Grid>
                    </DialogContent>
                )}
            </Dialog>
        </>
    );
};

Self.displayName = getTsxFileName(import.meta.url);
export default Self;
